import { ArrowBack, NavigateNext, PostAdd } from "@mui/icons-material";
import {
  Box,
  Breadcrumbs,
  CircularProgress,
  Grid,
  Pagination,
  PaginationItem,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { css, StyleSheet } from "aphrodite";
import { useEffect, useMemo, useRef, useState } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import CustomDatagrid from "../../../components/datagrid/CustomDatagrid";
import Colors from "../../../components/constants/Colors";
import { FilterPlanoContas } from "../../../components/filter/FilterPlanoContas";
import { usePaginationQuery } from "../../../helpers/pagination";
import axios from "axios";
import { headers } from "../../../components/core/store/localStorage";
import errorHandler from "../../../components/core/store/errorHandler";
import ErrorModal from "../../../components/modals/ErrorModal";
import { ConfirmModal } from "../../../components/modals/ConfirmModal";

const currentUser = sessionStorage.getItem("userName");
const currentEmp = sessionStorage.getItem("empName");
const API_URL = sessionStorage.getItem("apiUrl");
const PAGE_SIZE = 30;

export const PlanoContas = () => {
  const location = useLocation();
  const navigate = useNavigate();

  const { setQueryParams } = usePaginationQuery();

  const query = useMemo(
    () => new URLSearchParams(location.search),
    [location.search],
  );

  const page = Number(query.get("page") || 1);
  const filter = query.get("filtro");
  const isMobile = useMediaQuery("(max-width: 375px)");

  const [head, setHead] = useState([]);
  const [rows, setRows] = useState([]);
  const [totals, setTotals] = useState({});
  const [totalPages, setTotalPages] = useState(0);

  const [isSearch, setIsSearch] = useState(true);
  const [loading, setLoading] = useState(false);
  const [notFound, setNotFound] = useState(false);
  const [showGrid, setShowGrid] = useState(false);

  const [titleLoading, setTitleLoading] = useState("Carregando...");
  const [contaContabil, setContaContabil] = useState("");
  const [descricao, setDescricao] = useState("");
  const [tipoMovimento, setTipoMovimento] = useState("");
  const [analitica, setAnalitica] = useState(false);
  const [searchValue, setSearchValue] = useState(filter || "");
  const [inativo, setInativo] = useState(false);

  const [hasSearched, setHasSearched] = useState(false);
  const [openPayloadModal, setOpenPayloadModal] = useState(false);
  const [payloadErrors, setPayloadErrors] = useState([]);
  const [selectedRow, setSelectedRow] = useState(null);
  const [messageModal, setMessageModal] = useState("");
  const [openConfirmModal, setOpenConfirmModal] = useState(false);
  const [isEdit, setIsEdit] = useState(false);

  const [errors, setErrors] = useState({});
  const contaContabilRef = useRef(null);
  const descricaoRef = useRef(null);
  const tipoMovimentoRef = useRef(null);

  useEffect(() => {
    if (API_URL === null) window.location.href = "/";
  }, []);

  useEffect(() => {
    if (!hasSearched) return;
    fetchPlanoContas(page, filter);
  }, [page, filter, hasSearched]);

  const fetchPlanoContas = async (pageToFetch) => {
    setLoading(true);
    setNotFound(false);

    try {
      const url = searchValue?.trim()
        ? `${API_URL}/planocontas?page=${pageToFetch}&filtro=${encodeURIComponent(
            searchValue.trim(),
          )}`
        : `${API_URL}/planocontas?page=${pageToFetch}`;

      const response = await axios.get(url, headers());

      const payload = response?.data || {};

      const data = payload.data || [];
      const pages = Number(payload.totalPages || 1);

      setTotalPages(pages);

      if (pageToFetch > pages) {
        setQueryParams({ page: pages }, true);
        return;
      }

      if (data.length === 0) {
        setNotFound(true);
        setShowGrid(false);
        if (pageToFetch > 1) setQueryParams({ page: 1 }, true);
        return;
      }

      setShowGrid(true);
      setHead(payload.columns || []);
      setRows(data);
      setTotals(payload.totalizador || {});
      setNotFound(false);
    } catch (error) {
      setShowGrid(false);
      console.log(error);
      errorHandler(error);
    } finally {
      setLoading(false);
    }
  };

  const handleEdit = (row) => {
    setIsEdit(true);
    setErrors({});
    setIsSearch(false);
    setContaContabil(row.id);
    setDescricao(row.name);
    setTipoMovimento(row.tipoMovimento);
    setAnalitica(row.analitica === "S" ? true : false);
    setInativo(row.inativo === "Cancelado" ? true : false);
    setShowGrid(false);
  };

  const handleDelete = (row) => {
    setSelectedRow(row);
    setOpenConfirmModal(true);
    row.inativo === "Cancelado"
      ? setMessageModal("Deseja realmente ativar este plano de contas?")
      : setMessageModal("Deseja realmente desativar este plano de contas?");
  };

  const handleConfirmDelete = async () => {
    setLoading(true);
    setShowGrid(false);

    let novoStatusInativo;

    selectedRow.inativo === "Cancelado"
      ? (novoStatusInativo = false)
      : (novoStatusInativo = true);

    let novaAnalitica;
    selectedRow.analitica === "S"
      ? (novaAnalitica = true)
      : (novaAnalitica = false);

    const payload = {
      id: selectedRow.id,
      name: selectedRow.name,
      tipoMovimento: selectedRow.tipoMovimento,
      analitica: novaAnalitica,
      inativo: novoStatusInativo,
    };

    try {
      await axios.post(`${API_URL}/planocontas`, payload, headers());
      fetchPlanoContas(page);
    } catch (err) {
      errorHandler(err);
      setPayloadErrors([err.response.data.message]);
      setOpenPayloadModal(true);
    } finally {
      setLoading(false);
      setShowGrid(true);
      setOpenConfirmModal(false);
    }
  };

  const searchPlanoContas = () => {
    setHasSearched(true);
    setQueryParams({ page: 1, filtro: searchValue }, false);
  };

  const newPlanoContas = () => {
    setLoading(false);
    setIsSearch(false);
    setNotFound(false);
    setShowGrid(false);
    setContaContabil("");
    setSearchValue("");
    setHasSearched(false);
    setTotalPages(0);
    setRows([]);
    setHead([]);
    setInativo(false);
    setDescricao("");
    setAnalitica(false);
    setTipoMovimento("");
  };

  const cadPlanoContas = async () => {
    const payload = {
      id: contaContabil.trim(),
      name: descricao.trim(),
      tipoMovimento: tipoMovimento,
      analitica: analitica,
      inativo,
    };

    const required = (cond, msg, fieldName, ref) => {
      if (cond) return false;
      setPayloadErrors([msg]);
      setOpenPayloadModal(true);
      setErrors((prev) => ({ ...prev, [fieldName]: true }));
      ref.current?.focus();
      return true;
    };

    if (
      required(
        !!contaContabil,
        "Conta contábil é obrigatória.",
        "contaContabil",
        contaContabilRef,
      )
    )
      return;
    if (
      required(
        !!descricao,
        "Descrição é obrigatória.",
        "descricao",
        descricaoRef,
      )
    )
      return;
    if (
      required(
        !!tipoMovimento,
        "Tipo de movimento é obrigatório.",
        "tipoMovimento",
        tipoMovimentoRef,
      )
    )
      return;

    try {
      setLoading(true);
      await axios.post(`${API_URL}/planocontas`, payload, headers());
      setOpenPayloadModal(false);
      if (isEdit) {
        setIsEdit(false);
        handleBack();
      } else {
        setContaContabil("");
      }
      setDescricao("");
      setTipoMovimento("");
      setAnalitica(false);
      setInativo(false);
      // handleBack();
    } catch (error) {
      errorHandler(error);
      setPayloadErrors([error.response.data.message]);
      setOpenPayloadModal(true);
    } finally {
      setLoading(false);
    }
  };

  const handleBack = () => {
    setErrors({});
    setIsSearch(true);
    setNotFound(false);
    setLoading(false);
    setShowGrid(false);
    setHasSearched(false);
    setSearchValue("");
    setDescricao("");
    setContaContabil("");
    setAnalitica(false);
    setTipoMovimento("");
  };

  return (
    <>
      <Box sx={{ flexGrow: 1 }}>
        <Grid wrap="wrap">
          <Grid
            size={{ xs: 6, sm: 7, md: 8, xl: 10 }}
            style={{
              display: "flex",
              justifyContent: "space-between",
              alignItems: "center",
            }}
          >
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <Breadcrumbs
                separator={<NavigateNext fontSize="small" />}
                aria-label="breadcrumb"
              >
                <Typography
                  className={css(styles.pageTitle)}
                  sx={{ fontWeight: 400, color: "#828282" }}
                >
                  Contas a Pagar
                </Typography>
                <Typography className={css(styles.pageTitle)}>
                  Cadastros
                </Typography>

                <Typography className={css(styles.pageTitle)}>
                  Plano de Contas
                </Typography>
              </Breadcrumbs>
            </div>
            <div style={{ display: "flex", gap: "5px" }}>
              {isSearch && (
                <button
                  onClick={newPlanoContas}
                  className={css(styles.topBtn)}
                  title="Inclui"
                >
                  <PostAdd style={{ fontSize: "1.4rem" }} />
                </button>
              )}
              {!isSearch && (
                <button
                  onClick={handleBack}
                  className={css(styles.topBtn)}
                  title="Voltar"
                >
                  <ArrowBack style={{ fontSize: "1.4rem" }} />
                </button>
              )}
            </div>
          </Grid>
        </Grid>

        <hr
          style={{
            color: "#eee",
            // marginTop: "-0.55rem",
            marginBottom: "0.75rem",
          }}
        />

        <Box
          sx={{
            maxWidth: 1200,
            mx: "auto",
            display: "flex",
            flexDirection: "column",
            gap: 1,
          }}
        >
          <FilterPlanoContas
            setErrors={setErrors}
            errors={errors}
            contaContabilRef={contaContabilRef}
            descricaoRef={descricaoRef}
            tipoMovimentoRef={tipoMovimentoRef}
            isSearch={isSearch}
            searchValue={searchValue}
            setSearchValue={setSearchValue}
            contaContabil={contaContabil}
            setContaContabil={setContaContabil}
            descricao={descricao}
            setDescricao={setDescricao}
            tipoMovimento={tipoMovimento}
            setTipoMovimento={setTipoMovimento}
            analitica={analitica}
            setAnalitica={setAnalitica}
            searchPlanoContas={searchPlanoContas}
            cadPlanoContas={cadPlanoContas}
            handleBack={handleBack}
          />

          {showGrid ? (
            <>
              <Box
                sx={{
                  width: "100%",
                  display: "grid",
                }}
              >
                <CustomDatagrid
                  head={head}
                  row={rows}
                  totals={totals}
                  loading={loading}
                  hasTotal={true}
                  // onEyeClick={(row) => console.log("ver", row)}
                  onEditClick={(row) => handleEdit(row)}
                  columnOverrides={{
                    id: { minWidth: 150, maxWidth: 250 },
                    name: { minWidth: 200 },
                  }}
                  onDeleteClick={(row) => handleDelete(row)}
                />
                {totalPages > 1 && (
                  <Pagination
                    page={page}
                    count={totalPages}
                    showFirstButton
                    showLastButton
                    sx={{
                      mt: 1,
                      "& .MuiPaginationItem-root.Mui-selected": {
                        background: Colors.mainLayoutAtiveBackground,
                        color: "black",
                      },
                    }}
                    renderItem={(item) => (
                      <PaginationItem
                        {...item}
                        component={Link}
                        to={`/contas-a-pagar/tipo-de-fornecedor${
                          item.page
                            ? `?page=${item.page}${filter ? `&filtro=${filter}` : ""}`
                            : ""
                        }`}
                      />
                    )}
                  />
                )}
              </Box>
            </>
          ) : null}
        </Box>
        {loading ? (
          <Box
            width={"100%"}
            height={"45vh"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid
              item
              xs={12}
              md={12}
              textAlign={"center"}
              justifyContent={"center"}
              alignItems={"center"}
              bgcolor={"#cadccaff"}
              borderRadius={5}
              padding={5}
              color={Colors.noResultFont}
            >
              <div display={"flex"} style={{ marginTop: "-0.5rem" }}>
                <CircularProgress color="success" />
              </div>
              <div display={"flex"}>
                <p
                  style={{
                    color: "#4b4b4b",
                    fontSize: 14,
                    marginBottom: "-1rem",
                  }}
                >
                  {titleLoading}
                </p>
              </div>
            </Grid>
          </Box>
        ) : null}
        {notFound ? (
          <Box
            width={"100%"}
            height={"45vh"}
            display={"flex"}
            justifyContent={"center"}
            alignItems={"center"}
          >
            <Grid
              size={{ xs: 12, md: 12 }}
              height={{ md: "20%", xs: "50%" }}
              display={"inline-flex"}
              textAlign={"center"}
              justifyContent={"center"}
              alignItems={"center"}
              bgcolor={"lightyellow"}
              borderRadius={5}
              padding={5}
              color={Colors.noResultFont}
            >
              <p
                style={{
                  color: "#4b4b4b",
                  fontSize: 14,
                }}
              >
                Sua pesquisa não retornou nenhum resultado.
              </p>
            </Grid>
          </Box>
        ) : null}
        <ErrorModal
          open={openPayloadModal}
          onClose={() => setOpenPayloadModal(false)}
          errors={payloadErrors}
        />

        <ConfirmModal
          open={openConfirmModal}
          onClose={() => setOpenConfirmModal(false)}
          onConfirm={handleConfirmDelete}
          message={messageModal}
        />
      </Box>
    </>
  );
};

const styles = StyleSheet.create({
  pageTitle: {
    fontFamily: "Poppins",
    fontWeight: 600,
    color: "#4b4b4b",
    fontSize: 14,
    margin: 0,
  },
  head: {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    lineHeight: 1.5,
  },

  btn: {
    backgroundColor: "#669966",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 12,
    fontWeight: 600,
    textAlign: "center",
    textDecoration: "none",
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#669966",
    borderRadius: 4,
    marginTop: "-0.5rem",
    cursor: "pointer",

    padding: "5px 8px 3px 8px",
    transitionDuration: "0.2s",

    ":hover": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
    },
    ":active": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
      opacity: 0.6,
    },
  },
  topBtn: {
    backgroundColor: "#669966",
    cursor: "pointer",
    color: "#fff",
    fontFamily: "Poppins",
    fontSize: 14,
    fontWeight: 600,
    borderStyle: "solid",
    borderWidth: 0.5,
    borderColor: "#669966",
    borderRadius: 6,
    display: "flex !important",
    justifyContent: "center !important",
    alignContent: "center !important",
    padding: "4px 8px",
    transitionDuration: "0.2s",
    ":hover": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
    },
    ":active": {
      color: "#669966",
      backgroundColor: "transparent !important",
      borderColor: "#669966 !important",
      opacity: 0.6,
    },
    "@media (max-width: 375px)": {
      fontSize: 10,
      padding: "0.3rem 0.5rem",
    },
  },
});
